import type { Answers } from './constants';
import { graphql } from '@/gql';
import type {
  AddressInput,
  CreateDiagnosisInputChildV20240229,
  CreateDiagnosisInputPartnerV20240229,
  CreateDiagnosisInputV20240229,
  CreateKarteInput,
} from '@/gql/graphql';
import { formatISO, parse } from 'date-fns';

const parse8DigitDate = (digits: string): Date => {
  return parse(digits, 'yyyyMMdd', new Date());
};

const getPartnerInputFromAnswers = (
  answers: Answers
): Nullable<CreateDiagnosisInputPartnerV20240229> => {
  if (answers.hasPartner === false) return null;
  if (answers.partnerBirthday == null || answers.partnerAnnualIncome == null) {
    throw new Error('partner info is requred');
  }
  return {
    birthday: formatISO(parse8DigitDate(answers.partnerBirthday)) as DateString,
    annualIncome: Number.parseInt(answers.partnerAnnualIncome, 10) * 10000,
  };
};

const getChildrenInputFromAnswers = (
  answers: Answers
): CreateDiagnosisInputChildV20240229[] => {
  if (answers.hasChild === false) return [];
  // 子どもの人数を多く入れてから人数を減らすと、回答自体は残るが、子供の人数は変更される。
  // そのため、子供の人数が変わった場合は、子供の人数以上の回答は削除する。
  if (answers.countOfChildren == null)
    throw new Error('countOfChildren is required');
  const answeredChildren: CreateDiagnosisInputChildV20240229[] = [];
  if (answers.firstChildBirthDay != null && answers.firstChildGender != null) {
    answeredChildren.push({
      birthday: formatISO(
        parse8DigitDate(answers.firstChildBirthDay)
      ) as DateString,
      gender: answers.firstChildGender,
    });
  }
  if (
    answers.secondChildBirthDay != null &&
    answers.secondChildGender != null
  ) {
    answeredChildren.push({
      birthday: formatISO(
        parse8DigitDate(answers.secondChildBirthDay)
      ) as DateString,
      gender: answers.secondChildGender,
    });
  }
  if (answers.thirdChildBirthDay != null && answers.thirdChildGender != null) {
    answeredChildren.push({
      birthday: formatISO(
        parse8DigitDate(answers.thirdChildBirthDay)
      ) as DateString,
      gender: answers.thirdChildGender,
    });
  }
  if (
    answers.fourthChildBirthDay != null &&
    answers.fourthChildGender != null
  ) {
    answeredChildren.push({
      birthday: formatISO(
        parse8DigitDate(answers.fourthChildBirthDay)
      ) as DateString,
      gender: answers.fourthChildGender,
    });
  }
  if (answers.fifthChildBirthDay != null && answers.fifthChildGender != null) {
    answeredChildren.push({
      birthday: formatISO(
        parse8DigitDate(answers.fifthChildBirthDay)
      ) as DateString,
      gender: answers.fifthChildGender,
    });
  }
  const children = answeredChildren.slice(0, answers.countOfChildren);
  if (children.length !== answers.countOfChildren) {
    throw new Error('children count is invalid');
  }

  return children;
};

const getAddressInputFromAnswers = (answers: Answers): AddressInput => {
  if (answers.postalCode == null) throw new Error('postalCode is required');
  if (answers.address == null) throw new Error('address is required');
  if (answers.address.prefecture == null)
    throw new Error('prefecture is required');
  if (answers.address.city == null) throw new Error('city is required');
  if (answers.address.town == null) throw new Error('town is required');
  if (answers.address.jisx0402 == null) throw new Error('jisx0402 is required');

  return {
    postalCode: answers.postalCode,
    prefecture: answers.address.prefecture, // FIXME: jisx0401で渡さないとバックエンド的にはどうしようもなくね？
    city: answers.address.city,
    town: answers.address.town,
    jisx0402: answers.address.jisx0402,
  };
};

const getCreateDiagnosisInputFromAnswers = (
  answers: Answers
): CreateDiagnosisInputV20240229 => {
  if (answers.gender == null) throw new Error('gender is required');

  if (answers.jobId == null) throw new Error('jobId is required');

  if (answers.birthday == null) throw new Error('birthday is required');

  if (answers.savings == null) throw new Error('savings is required');
  const parsedSavings = Number.parseInt(answers.savings, 10) * 10000;
  if (isNaN(parsedSavings)) throw new Error('savings is invalid');

  if (answers.annualIncome == null) throw new Error('annualIncome is required');
  const parsedAnnualIncome = Number.parseInt(answers.annualIncome, 10) * 10000;
  if (isNaN(parsedAnnualIncome)) throw new Error('annualIncome is invalid');

  if (answers.monthlySavings == null)
    throw new Error('monthlySavings is required');
  const parsedMonthlySavings =
    Number.parseInt(answers.monthlySavings, 10) * 10000;
  if (isNaN(parsedMonthlySavings)) throw new Error('monthlySavings is invalid');

  if (answers.investment == null) throw new Error('investment is required');
  const parsedInvestment = Number.parseInt(answers.investment, 10) * 10000;
  if (isNaN(parsedInvestment)) throw new Error('investment is invalid');

  if (answers.monthlyRentCost == null)
    throw new Error('monthlyRentCost is required');
  const parsedMonthlyRentCost =
    Number.parseInt(answers.monthlyRentCost, 10) * 10000;
  if (isNaN(parsedMonthlyRentCost))
    throw new Error('monthlyRentCost is invalid');

  if (answers.loanBalance == null) throw new Error('loanBalance is required');
  const parsedLoanBalance = Number.parseInt(answers.loanBalance, 10) * 10000;
  if (isNaN(parsedLoanBalance)) throw new Error('loanBalance is invalid');

  if (answers.monthlyInsurance == null)
    throw new Error('monthlyInsurance is required');
  const parsedMonthlyInsurance = Number.parseInt(answers.monthlyInsurance, 10);
  if (isNaN(parsedMonthlyInsurance))
    throw new Error('monthlyInsurance is invalid');

  if (answers.thinksToughTimesBringOpportunity == null)
    throw new Error('thinksToughTimesBringOpportunity is required');

  if (answers.thinksWhatDoneIsDone == null)
    throw new Error('thinksWhatDoneIsDone is required');

  if (answers.isInterestedInNisaOrIdeco == null)
    throw new Error('isInterestedInNisaOrIdeco is required');

  if (answers.hasGoalInFuture == null)
    throw new Error('hasGoalInFuture is required');

  if (answers.takesThreeOrMoreLessons == null)
    throw new Error('takesThreeOrMoreLessons is required');

  if (answers.followsTheLatestTrends == null)
    throw new Error('followsTheLatestTrends is required');

  const address = getAddressInputFromAnswers(answers);
  const partner = getPartnerInputFromAnswers(answers);
  const children = getChildrenInputFromAnswers(answers);

  return {
    gender: answers.gender,
    jobId: answers.jobId,
    address,
    birthday: formatISO(parse8DigitDate(answers.birthday)) as DateString,
    saving: parsedSavings,
    annualIncome: parsedAnnualIncome,
    investment: parsedInvestment,
    partner: partner,
    savingMonth: parsedMonthlySavings,
    children,
    monthlyRentCost: parsedMonthlyRentCost,
    loanBalance: parsedLoanBalance,
    monthlyInsurance: parsedMonthlyInsurance,
    thinksToughTimesBringOpportunity: answers.thinksToughTimesBringOpportunity,
    thinksWhatDoneIsDone: answers.thinksWhatDoneIsDone,
    isInterestedInNisaOrIdeco: answers.isInterestedInNisaOrIdeco,
    hasGoalInFuture: answers.hasGoalInFuture,
    takesThreeOrMoreLessons: answers.takesThreeOrMoreLessons,
    followsTheLatestTrends: answers.followsTheLatestTrends,
  };
};

const getKarteInputFromAnswers = (answers: Answers): CreateKarteInput => {
  if (answers.karteTopics == null) throw new Error('karteTopics is required');
  if (answers.karteComment == null) throw new Error('karteComment is required');

  return {
    userTopics: answers.karteTopics,
    userComment: answers.karteComment,
  };
};

export const useCreateDiagnosis = (answers: Ref<Answers>) => {
  const { isExclusive } = useExclusive();
  const { mutate: createDiagnosisWithKarte } = useMutation(
    graphql(`
      mutation CreateDiagnosisWithKarte(
        $diagnosisInput: CreateDiagnosisInputV20240229!
        $karteInput: CreateKarteInput!
      ) {
        createDiagnosisV20240229AndKarte(
          diagnosisInput: $diagnosisInput
          karteInput: $karteInput
        ) {
          id
          currentKarte {
            id
          }
          latestDiagnosis {
            uuid
            resultV2 {
              type
              cluster {
                householdStructure
                residentialArea {
                  prefecture
                  city
                  town
                }
              }
              householdAnnualIncomeRank
              monthlySavingRank
              assetsRank
            }
            aiMatchScore {
              ...UseAiMatchScoreAiMatchScore
              lifePlanningMatchingScore
              lifePlanningExpectedContractPriceScore
              onetimeSaleMatchingScore
              onetimeSaleExpectedContractPriceScore
              householdImprovementMatchingScore
              householdImprovementExpectedContractPriceScore
              sellingHomeMatchingScore
              sellingHomeExpectedContractPriceScore
              realEstateInvestmentMatchingScore
              realEstateInvestmentExpectedContractPriceScore
              jobChangeMatchingScore
              jobChangeExpectedContractPriceScore
            }
          }
        }
      }
    `)
  );

  const { mutate: createDiagnosisOnly } = useMutation(
    graphql(`
      mutation CreateDiagnosis(
        $diagnosisInput: CreateDiagnosisInputV20240229!
      ) {
        createDiagnosisV20240229(diagnosisInput: $diagnosisInput) {
          latestDiagnosis {
            uuid
          }
        }
      }
    `)
  );

  const createDiagnosisResult =
    ref<
      NonNullable<Awaited<ReturnType<typeof createDiagnosisWithKarte>>>['data']
    >(null);

  const create = async (): Promise<
    { ok: false } | { ok: true; uuid: string }
  > => {
    const diagnosisInput = getCreateDiagnosisInputFromAnswers(answers.value);

    if (isExclusive.value) {
      const result = await createDiagnosisOnly({ diagnosisInput });
      if (!result) {
        captureException(new Error('createDiagnosisOnly result is empty'));
        return { ok: false };
      }
      const { data } = result;
      if (data?.createDiagnosisV20240229?.latestDiagnosis?.uuid == null) {
        captureException(new Error('createDiagnosisOnly data is empty'));
        return { ok: false };
      }
      return {
        ok: true,
        uuid: data.createDiagnosisV20240229.latestDiagnosis.uuid,
      };
    }

    const karteInput = getKarteInputFromAnswers(answers.value);
    const result = await createDiagnosisWithKarte({
      diagnosisInput,
      karteInput,
    });
    if (!result) {
      captureException(new Error('createDiagnosisWithKarte result is empty'));
      return { ok: false };
    }
    const { data } = result;
    if (!data) {
      captureException(new Error('createDiagnosisWithKarte data is empty'));
      return { ok: false };
    }
    const uuid = data.createDiagnosisV20240229AndKarte?.latestDiagnosis?.uuid;
    if (!uuid) {
      captureException(
        new Error('createDiagnosisWithKarte result uuid is empty')
      );
      return { ok: false };
    }
    createDiagnosisResult.value = data;
    return { ok: true, uuid };
  };

  return { create, createDiagnosisResult: readonly(createDiagnosisResult) };
};
